import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import Slider from 'react-slick';
import { AuthState, IMSubscriptionPlan } from "@/types";
import { ROUTES } from '@/config/routes';
import useSubscriptionPlans from '@/hooks/useSubscriptionPlans';
import LoaderIcon from "@/images/icons/Loader";
import { loadStripe } from '@stripe/stripe-js';
import { fetchAPI } from '@/api';
import useSeamlessLogin from '@/hooks/useSeamlessLogin';
import ArrowLeftCircle from '@/images/icons/ArrowLeftCircle';
import ChevronLeft from '@/images/icons/ChevronLeft';
import AnnualUpgradeModal from '@/components/AnnualUpgradeModal';
import ThanksModal from '@/pages/ManageSubscriptionNew/ThanksModal';
import ErrorModal from '@/components/ErrorModal';
import { isCanceledOrEndingSubscription, requestCheckoutSession } from '@/utils/utils';
import BillingPeriodToggle from '@/components/Subscription/BillingPeriodToggle';
import InvoiceSummary from '@/components/Subscription/InvoiceSummary';
import PlanSummary from '@/components/Subscription/PlanSummary';
import PlanCard from '@/components/Subscription/PlanCard';
import { filterIMPlans, getIMMonthlyEquivalent, navigateToSelectedPlan } from '@/utils/planUtils';
import { PrevArrow, NextArrow } from '@/components/Subscription/SliderArrows';

const ManageSubscriptionChangePlan = () => {
    const auth = useSelector((state: { auth: AuthState }) => state.auth);
    const {
        isLoading,
        subscription,
        currentPlan,
        plans,
        planPeriod,
        setPlanPeriod,
        trialEndDate,
        discountPercent,
    }: any = useSubscriptionPlans(auth);
    const { telegramUser } = useSeamlessLogin();
    const navigate = useNavigate();
    const sliderRef = useRef<Slider>(null);

    const currentRequestPlanIdRef = useRef<string | null>(null);
    const latestSelectedPlanIdRef = useRef<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const [invoicePreview, setInvoicePreview] = useState<any>(null);
    const [invoicePreviewOldPlan, setInvoicePreviewOldPlan] = useState<any>(null);
    const [invoicePreviewNewPlan, setInvoicePreviewNewPlan] = useState<any>(null);
    const [invoiceLoading, setInvoiceLoading] = useState<boolean>(false);
    const [futureInvoice, setFutureInvoice] = useState<any>(null);
    const [selectedPlan, setSelectedPlan] = useState<IMSubscriptionPlan | null>(null);
    const [upgradeAutomatically, setUpgradeAutomatically] = useState<boolean>(true);
    const [annualUpgradeModalOpen, setAnnualUpgradeModalOpen] = useState<boolean>(false);
    const [thanksModalOpen, setThanksModalOpen] = useState<boolean>(false);
    const [thanksModalMessage, setThanksModalMessage] = useState<string>('');
    const [thanksModalError, setThanksModalError] = useState<string>('');
    const [showMobileSummary, setShowMobileSummary] = useState<boolean>(false);
    const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);
    const [errorModal, setErrorModal] = useState<string>('');
    const [errorModalDescription, setErrorModalDescription] = useState<string>('');
    const [changePlanLoading, setChangePlanLoading] = useState<boolean>(false);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [is3DSecureLoading, setIs3DSecureLoading] = useState<boolean>(false);

    const isStripeUS = subscription?.method === "StripeUS";
    
    useEffect(() => {
        if (plans && plans.length > 0 && !selectedPlan) {
            if (currentPlan && currentPlan.period && planPeriod !== currentPlan.period) {
                setPlanPeriod(currentPlan.period);
                return;
            }

            const availablePlans = filterIMPlans(plans, planPeriod, currentPlan?.id);

            if (availablePlans.length > 0) {
                const planToSelect = availablePlans[0];
                setSelectedPlan(planToSelect);
                onChangePlanPreview(planToSelect);
                setIsInitialized(true);
            } else {
                const otherPeriod = planPeriod === 'month' ? 'year' : 'month';
                const otherPeriodPlans = filterIMPlans(plans, otherPeriod, currentPlan?.id);

                if (otherPeriodPlans.length > 0) {
                    setPlanPeriod(otherPeriod);
                }
            }
        }
    }, [plans, planPeriod, selectedPlan, currentPlan, isStripeUS]);

    useEffect(() => {
        if (selectedPlan) {
            if (selectedPlan.period !== planPeriod) {
                setSelectedPlan(null);
                setInvoicePreviewOldPlan(null);
                setInvoicePreviewNewPlan(null);
                setInvoicePreview(null);

                currentRequestPlanIdRef.current = null;
            }

            const equivalentPlan = plans?.find((plan: IMSubscriptionPlan) =>
                plan.period === planPeriod && plan.name === selectedPlan.name
            );

            if (equivalentPlan) {
                if (equivalentPlan.id === currentPlan?.id) {
                    const availablePlans = filterIMPlans(plans, planPeriod, currentPlan?.id);

                    if (availablePlans.length > 0) {
                        const alternativePlan = availablePlans[0];

                        if (currentRequestPlanIdRef.current === alternativePlan.id) {
                            return;
                        }

                        setSelectedPlan(alternativePlan);
                        onChangePlanPreview(alternativePlan);
                        navigateToSelectedPlan(alternativePlan, filteredPlans, sliderRef, sliderSettings);
                        return;
                    } else {
                        return;
                    }
                }

                if (currentRequestPlanIdRef.current === equivalentPlan.id) {
                    return;
                }

                if (!isInitialized && currentPlan && currentPlan.period === planPeriod) {
                    return;
                }

                setSelectedPlan(equivalentPlan);
                onChangePlanPreview(equivalentPlan);
                navigateToSelectedPlan(equivalentPlan, filteredPlans, sliderRef, sliderSettings);
            }
        }
    }, [planPeriod, isInitialized, isStripeUS]);

    useEffect(() => {
        if (!isLoading && currentPlan && currentPlan.period) {
            setPlanPeriod(currentPlan.period);
        }
    }, [isLoading, currentPlan]);

    useEffect(() => {
        if ((subscription && Object.keys(subscription).length === 0) || isCanceledOrEndingSubscription(subscription)) {
            navigate(ROUTES.getSubscriptionRoutes().manageSubscription);
        }
    }, [subscription]);

    const onChangePlanPreview = async (plan: IMSubscriptionPlan) => {
        if (currentPlan && !isStripeUS) {
            latestSelectedPlanIdRef.current = plan.id;

            if (currentRequestPlanIdRef.current === plan.id) {
                return;
            }

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }

            if (plan.id !== invoicePreviewNewPlan?.id) {
                currentRequestPlanIdRef.current = plan.id;

                setInvoiceLoading(true);

                if (!invoicePreviewNewPlan || invoicePreviewNewPlan.id !== plan.id) {
                    setInvoicePreviewOldPlan(null);
                    setInvoicePreviewNewPlan(null);
                    setInvoicePreview(null);
                }

                fetchInvoiceData({ oldIMPlanId: currentPlan?.id, newIMPlanId: plan.id });
            }
        }
    }

    const fetchInvoiceData = async ({ oldIMPlanId, newIMPlanId }: any) => {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
            const response = await fetchAPI(
                "/billing/switch-plan/invoice",
                auth.user?.token,
                { oldIMPlanId, newIMPlanId },
                { signal: abortController.signal }
            );

            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            const data = await response.json();

            if (latestSelectedPlanIdRef.current !== newIMPlanId) {
            } else {
                if (data) {
                    setInvoicePreview(data.invoice);
                    setInvoicePreviewOldPlan(data.oldPlan);
                    setInvoicePreviewNewPlan(data.newPlan);

                    if (data.futureInvoice) {
                        setFutureInvoice(data.futureInvoice);
                    }

                    setInvoiceLoading(false);
                }
            }
        } catch (error: any) {
            if (error.name === 'AbortError') {
                return;
            }

            let errorMessage = 'An unexpected error occurred';
            if (error.response?.message && error.name !== "AbortError") {
                errorMessage = error.response.message;
            }
            console.log(errorMessage);

            if (latestSelectedPlanIdRef.current === newIMPlanId) {
                setInvoiceLoading(false);
            }
        } finally {
            if (abortControllerRef.current === abortController) {
                abortControllerRef.current = null;
            }

            if (currentRequestPlanIdRef.current === newIMPlanId) {
                currentRequestPlanIdRef.current = null;
            }
        }
    };

    const handle3DSecure = async (clientSecret: string, paymentIntentId: string, stripePublicKey: string) => {
        setIs3DSecureLoading(true);
        
        try {
            if (!stripePublicKey) {
                throw new Error('Stripe public key not found');
            }

            const stripe = await loadStripe(stripePublicKey);
            if (!stripe) {
                throw new Error('Failed to load Stripe');
            }

            const { error } = await stripe.confirmCardPayment(clientSecret);
            
            if (error) {
                setErrorModal('Authentication failed');
                setErrorModalDescription(error.message || '');
                setErrorModalOpen(true);
                return;
            }

            setThanksModalMessage("You have successfully changed your plan!");
            setThanksModalOpen(true);

            setTimeout(() => {
                navigate(ROUTES.getSubscriptionRoutes().manageSubscription);
            }, 3000);
        } catch (error: any) {
            setErrorModal('An unexpected error occurred during authentication');
            setErrorModalDescription(error.message || '');
            setErrorModalOpen(true);
        } finally {
            setIs3DSecureLoading(false);
        }
    };

    const filteredPlans = filterIMPlans(plans, planPeriod, currentPlan?.id);

    const getMonthlyEquivalent = (yearlyPlan: IMSubscriptionPlan) => {
        if (!plans) return null;
        return getIMMonthlyEquivalent(yearlyPlan, plans);
    };

    const sliderSettings = {
        dots: false,
        infinite: false,
        speed: 500,
        slidesToShow: 3,
        slidesToScroll: 1,
        arrows: true,
        prevArrow: <PrevArrow />,
        nextArrow: <NextArrow />
    };

    const onRequestCheckoutSession = async (plan: IMSubscriptionPlan | null) => {
        setChangePlanLoading(true);
        if (plan) {
            let { name, period } = plan;
            let toRedirect = false;

            try {
                const response = await requestCheckoutSession({
                    endpoint: `${process.env.REACT_APP_API_URL}/stripeCheckoutSession`,
                    name: name,
                    isYearly: period === 'year',
                    region: 'CY',
                    telegramUser,
                });

                if (response.ok && response.status === 200) {
                    const responseData = await response.json();

                    if (responseData.sessionId) {
                        let stripePublicKey = responseData.stripePublicKey || process.env.REACT_APP_STRIPE_KEY;
                        const stripe = await loadStripe(stripePublicKey);

                        if (stripe) {
                            const result = await stripe.redirectToCheckout({
                                sessionId: responseData.sessionId,
                            });

                            if (result.error) {
                                console.log(`Stripe: ${result.error.message}`);
                                toRedirect = true;
                            }
                        } else {
                            console.log("There was an issue processing your payment. Please try again later.");
                            toRedirect = true;
                        }
                    } else {
                        toRedirect = true;
                    }
                }
            } catch (error) {
                console.log(error);
                toRedirect = true;
            } finally {
                setChangePlanLoading(false);
            }

            if (toRedirect) {
                navigate('/manage-subscription');
            }
        }
    };

    const onChangePlan = async (plan: IMSubscriptionPlan | null) => {
        if (subscription && Object.keys(subscription).length > 0 && plan) {
            if (isStripeUS) {
                await onRequestCheckoutSession(plan);
            } else {
                setChangePlanLoading(true);
                try {
                    const response = await fetchAPI("/billing/switch-plan/confirm", auth?.user?.token, { newIMPlanId: plan.id }) as any;
                    const data = await response.json();

                    if (response.status === 200) {
                        setThanksModalMessage(data.message || "You have successfully changed your plan!");
                        setThanksModalOpen(true);

                        setTimeout(() => {
                            navigate(ROUTES.getSubscriptionRoutes().manageSubscription);
                        }, 3000);
                    } else if (response.status === 202 && data.requiresAction) {
                        // 3D Secure authentication required - show Stripe modal
                        if (data.clientSecret && data.paymentIntentId && data.stripePublicKey) {
                            await handle3DSecure(data.clientSecret, data.paymentIntentId, data.stripePublicKey);
                        } else {
                            throw new Error('Invalid 3D Secure response data');
                        }
                    } else {
                        setErrorModal('An unexpected error occurred');
                        setErrorModalDescription(data.message || '');
                        setErrorModalOpen(true);
                    }
                } catch (error: any) {
                    console.log(error.message || error);
                    setErrorModal('An unexpected error occurred');
                    setErrorModalDescription(error.message || '');
                    setErrorModalOpen(true);
                } finally {
                    setChangePlanLoading(false);
                }
            }
        }
    };

    const submitCheckout = () => {
        if (planPeriod === 'month') {
            setAnnualUpgradeModalOpen(true);
        } else {
            onChangePlan(selectedPlan);
        }
    };

    const ContinueButton = ({ className = "" }) => (
        <button
            onClick={() => submitCheckout()}
            disabled={changePlanLoading || is3DSecureLoading}
            className={`btn-primary font-founders text-2xl/[0.7] w-full h-12 pt-2 pb-2.5 outline-0 ${className}`}
        >
            {changePlanLoading || is3DSecureLoading ? (
                <div className="flex items-center justify-center py-1 pointer-events-none">
                    <LoaderIcon className="animate-rotation w-6 h-6 text-white" />
                </div>
            ) : (
                <span>Continue</span>
            )}
        </button>
    );

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-1 pointer-events-none w-full h-screen">
                <LoaderIcon className="animate-rotation w-10 h-10 text-brand-green" />
            </div>
        );
    }

    return (
        <div className="flex flex-col lg:flex-row h-screen" style={{ paddingTop: 'var(--notification-height, 0px)' }}>
            <div className={`w-full lg:w-[73%] overflow-y-auto ${showMobileSummary ? 'hidden lg:block' : 'block'}`}>
                <div className="flex min-h-screen text-[#4a4a4a]">
                    <section className="bg-brand-white flex items-stretch flex-col justify-between text-brand-green relative flex-1 max-w-full">
                        <Link className="flex items-center gap-1 lg:gap-3 mt-8 mb-2 lg:my-8 ml-3 lg:ml-6 group"
                              to={ROUTES.getSubscriptionRoutes().manageSubscription}>
                            <ChevronLeft className="lg:hidden w-5 h-5 rounded-full text-brand-green" />
                            <ArrowLeftCircle backgroundColor="#F7F6F2"
                                             className="hidden lg:block w-8 h-8 rounded-full text-brand-green" />
                            <span className="text-[11px]/[1.1] md:text-[14px]/[1.3] group-hover:translate-x-1 transition-transform duration-300">Plans & Billing</span>
                        </Link>
                        <div className="h-full pt-6 md:pt-14 pb-28 md:pb-14 px-4 md:px-6 xl:px-8">
                            <div className="basis-0 grow shrink md:px-2">
                                <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-10 xl:gap-4">
                                    <div className="max-w-[400px]">
                                        <h1 className="h1 text-3xl/[0.9] md:text-4xl/[0.9] xl:text-[4rem]/[0.8] mt-2 mb-3 md:mb-4 xl:mb-8">Change plan</h1>
                                        <div className="text-[13px]/[1.4] md:text-base/[1.3]">
                                            Change your subscription plan with ease.
                                            {discountPercent > 0 && (
                                                <div><b>High-volume customer bonus:</b> enjoy an exclusive <b>{discountPercent}% discount for 18 months</b> — and get <b>another 20% off</b> when you choose yearly billing.</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 lg:translate-y-1">
                                        <div className="flex justify-center md:mb-8">
                                            <BillingPeriodToggle
                                                period={planPeriod}
                                                onToggle={setPlanPeriod}
                                                className="lg:translate-y-1"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8 md:mt-14">
                                    <div className="slider-container relative">
                                        {/* Desktop view with slider (lg screens and up) */}
                                        <div className="hidden lg:block">
                                            <Slider ref={sliderRef} {...sliderSettings} className="pt-16 relative">
                                                {filteredPlans.map((plan: IMSubscriptionPlan, index: number) => (
                                                    <div key={`plan-desktop-${plan.id}-${index}`}
                                                         className={classNames(
                                                             'border rounded-[18px] p-5 xl:p-6 group transition-colors duration-300 ease-out relative h-full outline-0',
                                                             selectedPlan?.id === plan.id ? 'bg-[#FFFEDD] border-[#EAE9C2]' : 'bg-brand-white border-[#DFD8CD]',
                                                         )}
                                                         onClick={() => {
                                                             setSelectedPlan(filteredPlans[index]);
                                                             onChangePlanPreview(filteredPlans[index]);
                                                         }}>
                                                        <PlanCard
                                                            plan={plan}
                                                            selectedPlanId={selectedPlan?.id}
                                                            getMonthlyEquivalent={getMonthlyEquivalent}
                                                            expandable={true}
                                                            discountPercent={discountPercent}
                                                        />
                                                    </div>
                                                ))}
                                            </Slider>
                                        </div>

                                        {/* Mobile/tablet view with stacked cards (less than lg screens) */}
                                        <div className="lg:hidden space-y-4">
                                            {filteredPlans.map((plan: IMSubscriptionPlan, index: number) => (
                                                <div key={`plan-mobile-${plan.id}-${index}`}
                                                     className={classNames(
                                                         'border rounded-[18px] py-4 px-5 md:p-6 group transition-colors duration-300 ease-out relative',
                                                         selectedPlan?.id === plan.id ? 'bg-[#FFFEDD] border-[#EAE9C2]' : 'bg-brand-white border-[#DFD8CD]',
                                                     )}
                                                     onClick={() => {
                                                         setSelectedPlan(filteredPlans[index]);
                                                         onChangePlanPreview(filteredPlans[index]);
                                                     }}>
                                                    <PlanCard
                                                        plan={plan}
                                                        selectedPlanId={selectedPlan?.id}
                                                        getMonthlyEquivalent={getMonthlyEquivalent}
                                                        expandable={true}
                                                        discountPercent={discountPercent}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mobile Continue button at the bottom */}
                        {selectedPlan && (
                            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-brand-white p-4">
                                <button
                                    onClick={() => setShowMobileSummary(true)}
                                    className="btn-primary font-founders text-2xl/[0.7] w-full h-12 pt-2 pb-2.5 outline-0"
                                >
                                    <span>Continue</span>
                                </button>
                            </div>
                        )}
                    </section>
                </div>
            </div>

            {/* Mobile summary view */}
            <div className={`w-full overflow-y-auto bg-brand-white lg:hidden ${showMobileSummary ? 'block' : 'hidden'}`}>
                <div className="min-h-[calc(var(--vh,1vh)*100-var(--notification-height,0px))] flex flex-col">
                    <div
                        className="flex items-center gap-1 mt-8 mb-3 ml-3 group cursor-pointer"
                        onClick={() => setShowMobileSummary(false)}
                    >
                        <ChevronLeft className="w-5 h-5 rounded-full text-brand-green" />
                        <span className="text-[11px]/[1.1] md:text-[14px]/[1.3] group-hover:translate-x-1 transition-transform duration-300">Change plan</span>
                    </div>

                    <div className="flex flex-col flex-1 p-4">
                        <div className="font-founders text-3xl/[0.9] mb-6">Your new subscription</div>

                        {!isStripeUS && invoiceLoading && (
                            <div className="flex items-center justify-center py-1 pointer-events-none w-full h-full m-auto">
                                <LoaderIcon className="animate-rotation w-10 h-10 text-brand-green" />
                            </div>
                        )}

                        {isStripeUS && selectedPlan ? (
                            <div className="bg-[#F7F6F2] py-5 px-4 rounded-[14px] mb-6">
                                <PlanSummary plan={selectedPlan} trialEndDate={trialEndDate} />
                            </div>
                        ) : (
                            invoicePreviewNewPlan && invoicePreview && (
                                <div className="bg-[#F7F6F2] py-5 px-4 rounded-[14px] mb-6">
                                    <InvoiceSummary
                                        invoicePreview={invoicePreview}
                                        invoicePreviewNewPlan={invoicePreviewNewPlan}
                                        selectedPlan={selectedPlan}
                                        futureInvoice={futureInvoice}
                                        trialEndDate={trialEndDate}
                                        isMobile={true}
                                        discountPercent={discountPercent}
                                    />
                                </div>
                            )
                        )}

                        {((isStripeUS && selectedPlan) || (!isStripeUS && invoicePreviewNewPlan && invoicePreview)) && (
                            <ContinueButton className="mt-auto mb-8" />
                        )}
                    </div>
                </div>
            </div>

            {/* Desktop sidebar */}
            <div className="hidden lg:block w-[27%] bg-[#F7F6F2] sticky top-0 self-start h-full overflow-y-auto">
                {selectedPlan && (
                    <div className="flex flex-col md:py-8 xl:py-10 md:px-6 xl:px-8 h-full">
                        <div className="font-founders md:text-2xl/[0.7] xl:text-3xl/[0.9] md:mb-10 xl:mb-12">Your new subscription
                        </div>
                        {!isStripeUS && invoiceLoading && (
                            <div className="flex items-center justify-center py-1 pointer-events-none w-full h-full">
                                <LoaderIcon className="animate-rotation w-10 h-10 text-brand-green" />
                            </div>
                        )}

                        {isStripeUS ? (
                            <PlanSummary plan={selectedPlan} trialEndDate={trialEndDate} />
                        ) : (
                            invoicePreviewNewPlan && invoicePreview && (
                                <InvoiceSummary
                                    invoicePreview={invoicePreview}
                                    invoicePreviewNewPlan={invoicePreviewNewPlan}
                                    selectedPlan={selectedPlan}
                                    futureInvoice={futureInvoice}
                                    trialEndDate={trialEndDate}
                                    isMobile={false}
                                    discountPercent={discountPercent}
                                />
                            )
                        )}

                        {((isStripeUS && selectedPlan) || (!isStripeUS && invoicePreviewNewPlan && invoicePreview)) && (
                            <ContinueButton className="md:mb-6 xl:mb-10" />
                        )}
                    </div>
                )}
            </div>

            <AnnualUpgradeModal
                modalOpen={annualUpgradeModalOpen}
                setModalOpen={(isOpen) => setAnnualUpgradeModalOpen(isOpen)}
                selectedPlan={selectedPlan}
                plans={plans}
                getMonthlyEquivalent={getMonthlyEquivalent}
                discountPercent={discountPercent}
                onYearly={() => {
                    setPlanPeriod('year');

                    if (selectedPlan && plans) {
                        const yearlyPlan = plans.find((plan: IMSubscriptionPlan) =>
                            plan.period === 'year' && plan.name === selectedPlan.name
                        );

                        if (yearlyPlan) {
                            onChangePlan(yearlyPlan);
                        }
                    }
                }}
                onMonthly={() => onChangePlan(selectedPlan)}
            />
            <ThanksModal
                modalOpen={thanksModalOpen}
                setModalOpen={setThanksModalOpen}
                modalMessage={thanksModalMessage}
                modalError={thanksModalError}
                setModalMessage={setThanksModalMessage}
                setModalError={setThanksModalError}
                subscription={subscription && Object.keys(subscription).length > 0}
                isRedirect={true}
            />
            <ErrorModal
                modalOpen={errorModalOpen}
                setModalOpen={setErrorModalOpen}
                error={errorModal}
                description={errorModalDescription}
            />
        </div>
    );
};

export default ManageSubscriptionChangePlan;
